import { useEffect, useState } from "react";
import { UploadImageInput } from "#/components/chat/upload-image-input";
import { SubmitButton } from "#/components/shared/buttons/submit-button";
import Input from "#/components/shared/input";
import ThemedSpinner from "#/components/shared/spinner";
import ThemedSelect from "#/components/shared/themed-select";
import Typography from "#/components/shared/typography";
import { FILE_UPLOAD_ACCEPT } from "#/lib/fileUpload";
import { useToast } from "#/hooks/ToastContext";
import { useFileUploadContext } from "#/hooks/FileUploadContext";

const evidenceSourceTypes = [
  "peer_reviewed_paper",
  "clinical_guideline",
  "systematic_review",
  "meta_analysis",
  "clinical_trial",
  "expert_consensus",
  "textbook",
];

type UploadSourceDocumentFormProps = {
  onComplete?: () => void;
  compact?: boolean;
};

export function UploadSourceDocumentForm({
  onComplete,
  compact = false,
}: UploadSourceDocumentFormProps) {
  const { showToast } = useToast();
  const { files, setFiles, uploadSource, fileUploadResponse, fileUploading } =
    useFileUploadContext();
  const [name, setName] = useState("");
  const [docType, setDocType] = useState("textbook");

  useEffect(() => {
    if (fileUploadResponse) {
      showToast("Document uploaded", "success");
      onComplete?.();
    }
  }, [fileUploadResponse, onComplete, showToast]);

  const handleUploadLocally = (newFiles: File[]) => {
    setFiles(newFiles);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!files.length) return;
    uploadSource({
      files,
      additionalData: {
        purpose: "medical_concept_document",
        description: name,
        sourceType: docType,
      },
    });
  };

  const formContent = (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          id="doc-name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />

        <ThemedSelect
          label="Document Type"
          options={evidenceSourceTypes.map((type) => ({
            value: type,
            label: type
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char) => char.toUpperCase()),
          }))}
          initialValue={docType}
          onChange={(e) => setDocType(e.target.value)}
        />
        <div className="flex flex-col gap-2">
          <UploadImageInput
            onUpload={handleUploadLocally}
            accept={FILE_UPLOAD_ACCEPT}
            button={
              <button className="flex items-center gap-2">
                <Typography variant="body1" className="text-terracotta">
                  Select Document
                </Typography>
              </button>
            }
          />
          {files.length > 0 && (
            <Typography variant="body2" className="text-terracotta">
              {files.map((f) => f.name).join(", ")}
            </Typography>
          )}
        </div>
        <SubmitButton
          onSubmit={handleSubmit}
          isDisabled={fileUploading || !name || !docType || !files.length}
        >
          Upload
        </SubmitButton>
      </form>
      {fileUploading && (
        <div className="flex justify-center mt-4">
          <ThemedSpinner size="sm" />
        </div>
      )}
    </>
  );

  if (compact) {
    return formContent;
  }

  return <div className="space-y-4">{formContent}</div>;
}
